import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { NewsArticle } from '@/types/news.types'
import { getColombianStartOfToday } from '@/utils/colombianTimezone'

export const selectNewsState = (state: RootState) => state.news
export const selectNewsArticles = (state: RootState) => selectNewsState(state).news
export const selectEventArticles = (state: RootState) => selectNewsState(state).events
export const selectNewsLoading = (state: RootState) => selectNewsState(state).loadingNews
export const selectEventLoading = (state: RootState) => selectNewsState(state).loadingEvents
export const selectNewsError = (state: RootState) => selectNewsState(state).errorNews
export const selectEventError = (state: RootState) => selectNewsState(state).errorEvents

/**
 * Timestamp de la fecha de vencimiento de un artículo.
 * Retorna `null` cuando el artículo no tiene fecha o no es parseable.
 */
const getExpirationTime = (article: NewsArticle): number | null => {
  if (!article.date) return null
  const time = new Date(article.date).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * Retorna las noticias y comunicados que deben verse en la web pública, ordenados
 * por fecha de vencimiento ascendente (el que vence primero aparece más arriba).
 *
 * - Los ya vencidos (`date` anterior al inicio del día en Colombia) se ocultan.
 *   Siguen existiendo en Firestore y en el panel de administración; solo dejan de
 *   mostrarse al público. Mismo criterio que `fetchEventArticles` para eventos.
 * - Los que no tienen fecha de vencimiento no se ocultan: quedan de últimos.
 *
 * Memoizado con `createSelector` para evitar re-renders innecesarios en consumidores
 * cuando otros slices cambian o cuando la referencia del array no varía.
 */
export const selectVisibleNewsArticles = createSelector([selectNewsArticles], (news) => {
  const startOfToday = getColombianStartOfToday().getTime()

  return news
    .filter((article) => {
      const expiration = getExpirationTime(article)
      return expiration === null || expiration >= startOfToday
    })
    .sort((a, b) => {
      const ta = getExpirationTime(a)
      const tb = getExpirationTime(b)
      if (ta === null && tb === null) return 0
      if (ta === null) return 1
      if (tb === null) return -1
      return ta - tb
    })
})
